import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import BlockedAnswers from "./BlockedAnswers";
import {
  PDPA_OPTOUT_ATTESTATION_TEXT,
  buildAttestationRecord,
  buildClinicConfirmationPayload,
  buildPdpaOptoutEvent,
} from "../lib/pdpaOptout.mjs";

interface ProfileRow {
  prenom: string;
  nom: string;
  telephone: string;
  email: string;
  twilio_numero?: string;
}

interface ChakraSkippedChat {
  chat_id: string;
  status: number;
}

interface ChakraPreviewMessage {
  chakra_message_id: string;
  chakra_chat_id?: string | null;
  utilisateur: string;
  role: string;
  created_at: string;
  text_preview: string;
  lu: boolean;
}

interface ChakraPreviewResult {
  total_fetched: number;
  importable: number;
  skipped: number;
  duplicates: number;
  missing_ids: number;
  skipped_chats: ChakraSkippedChat[];
  total_chats?: number | null;
  scanned_chats?: number | null;
  scan_complete?: boolean;
  chat_limit?: number | null;
  message_limit_reached?: boolean;
  before_date?: string | null;
  date_filter_cutoff?: string | null;
  date_filtered?: number;
  messages: ChakraPreviewMessage[];
}

interface ChakraImportResult {
  imported: number;
  skipped: number;
  skipped_chats?: ChakraSkippedChat[];
  total_chats?: number | null;
  scanned_chats?: number | null;
  scan_complete?: boolean;
  message_limit_reached?: boolean;
  before_date?: string | null;
  date_filter_cutoff?: string | null;
  date_filtered?: number;
}

interface ChakraImportJob {
  job_id: string;
  kind?: "preview" | "import";
  status: "queued" | "running" | "completed" | "failed";
  mode?: string;
  result?: ChakraImportResult | ChakraPreviewResult;
  error?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
}

type ProfileMode =
  | "full"
  | "settings-profile"
  | "settings-appearance"
  | "settings-labels"
  | "settings-blocked-answers";

const HIGHLIGHT_COLORS = [
  { value: "", label: "None", swatch: "bg-white border border-gray-300" },
  { value: "blue", label: "Blue", swatch: "bg-blue-400" },
  { value: "green", label: "Green", swatch: "bg-green-400" },
  { value: "yellow", label: "Yellow", swatch: "bg-yellow-300" },
  { value: "red", label: "Red", swatch: "bg-red-400" },
  { value: "purple", label: "Purple", swatch: "bg-purple-400" },
  { value: "pink", label: "Pink", swatch: "bg-pink-400" },
  { value: "indigo", label: "Indigo", swatch: "bg-indigo-400" },
  { value: "gray", label: "Gray", swatch: "bg-gray-300" },
];

function HighlightColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex flex-wrap gap-2">
        {HIGHLIGHT_COLORS.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => onChange(color.value)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              value === color.value
                ? "border-primary ring-2 ring-primary ring-offset-1"
                : "border-gray-200 hover:border-gray-400"
            }`}
          >
            <span className={`h-3 w-3 rounded-full ${color.swatch}`} />
            {color.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Profile({ mode = "full" }: { mode?: ProfileMode }) {
  const user = useUser();
  const [editing, setEditing] = useState(false);
  const [calendarType, setCalendarType] = useState<string | null>(null);
  const [clientData, setClientData] = useState<any | null>(null);
  const [hasViewedContract, setHasViewedContract] = useState(false);
  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    email: "",
    newPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [chakraMode, setChakraMode] = useState<"phone" | "chat" | "all">("phone");
  const [chakraPhoneNumber, setChakraPhoneNumber] = useState("");
  const [chakraChatId, setChakraChatId] = useState("");
  const [chakraBeforeDate, setChakraBeforeDate] = useState("");
  const [chakraAction, setChakraAction] = useState<"preview" | "import" | null>(null);
  const [chakraError, setChakraError] = useState<string | null>(null);
  const [chakraPreview, setChakraPreview] = useState<ChakraPreviewResult | null>(null);
  const [chakraImportJob, setChakraImportJob] = useState<ChakraImportJob | null>(null);
  const [chakraResult, setChakraResult] = useState<ChakraImportResult | null>(null);
  const chakraJobPollRef = useRef<number | null>(null);
  const [highlightUnread, setHighlightUnread] = useState<string>("blue");
  const [highlightRead, setHighlightRead] = useState<string>("");
  const [highlightSaving, setHighlightSaving] = useState(false);
  const [statusTagsEnabled, setStatusTagsEnabled] = useState(false);
  const [clinicStatuses, setClinicStatuses] = useState<Array<{ id: string; label: string; color: string }>>([]);
  const [statusesSaving, setStatusesSaving] = useState(false);
  const [pdpaAckChecked, setPdpaAckChecked] = useState(false);
  const [pdpaSaving, setPdpaSaving] = useState(false);

  const showProfileSection = mode === "full" || mode === "settings-profile";
  const showHighlightsSection = mode === "full" || mode === "settings-appearance";
  const showLabelsSection = mode === "full" || mode === "settings-labels";
  const showBlockedAnswersSection = mode === "settings-blocked-answers";
  const isEmbedded = mode !== "full";
  const chakraJobRunning = chakraImportJob?.status === "queued" || chakraImportJob?.status === "running";
  const chakraPreviewRunning = chakraJobRunning && chakraImportJob?.kind === "preview";
  const chakraImportRunning = chakraJobRunning && chakraImportJob?.kind !== "preview";
  const chakraLoading = chakraAction !== null || chakraJobRunning;
  const chakraJobStatusLabel = chakraPreviewRunning ? "Preview running" : "Import running";
  const chakraMissingRequiredInput =
    (chakraMode === "phone" && !chakraPhoneNumber.trim()) ||
    (chakraMode === "chat" && !chakraChatId.trim());
  const containerClass = isEmbedded
    ? "space-y-6 font-sans text-gray-800"
    : "mx-auto max-w-xl px-4 py-8 font-sans text-gray-800";
  const cardClass = isEmbedded
    ? "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    : "rounded-xl bg-white p-6 shadow";

  const loadProfile = async () => {
    if (!user) return;

    setLoading(true);

    const { data, error } = await supabase
      .from("profiles")
      .select("prenom, nom, telephone, email, twilio_numero")
      .eq("id", user.id)
      .single();

    const { data: calData } = await supabase
      .from("calendars")
      .select("type")
      .eq("client_id", user.id)
      .maybeSingle();

    const clinicTwilio = data?.twilio_numero;

    let client: any = null;
    let clientError: any = null;

    if (clinicTwilio) {
      const byTwilio = await supabase
        .from("clients")
        .select("*")
        .eq("twilio_numero", clinicTwilio)
        .maybeSingle();
      client = byTwilio.data;
      clientError = byTwilio.error;
    } else {
      const byId = await supabase
        .from("clients")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();
      client = byId.data;
      clientError = byId.error;
    }

    if (!error && data) {
      setForm((previous) => ({
        ...previous,
        ...data,
        email: user.email ?? data.email ?? "",
      }));
    }

    if (calData) {
      setCalendarType(calData.type);
    }

    if (client && !clientError) {
      setClientData(client);
      setHighlightUnread(client.highlight_unread || "blue");
      setHighlightRead(client.highlight_read || "");
      setStatusTagsEnabled(!!client.status_tags_enabled);
      if (Array.isArray(client.conversation_statuses)) {
        setClinicStatuses(client.conversation_statuses);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (user) loadProfile();
  }, [user]);

  const saveProfile = async () => {
    if (!user) return;

    const { error: profileError } = await supabase
      .from("profiles")
      .update({ prenom: form.prenom, nom: form.nom, telephone: form.telephone })
      .eq("id", user.id);
    if (profileError) return alert("Erreur profil: " + profileError.message);

    if (form.email && form.email !== user.email) {
      const { error } = await supabase.auth.updateUser({ email: form.email });
      if (error) return alert("Erreur email: " + error.message);
    }

    if (form.newPassword) {
      const { error } = await supabase.auth.updateUser({ password: form.newPassword });
      if (error) return alert("Erreur mot de passe: " + error.message);
    }

    setEditing(false);
    loadProfile();
  };

  const handleGoogleConnect = () => {
    window.location.href = `${import.meta.env.VITE_API_URL}/google/auth?user_id=${user?.id}`;
  };

  const handleGmailConnect = () => {
    window.location.href = `${import.meta.env.VITE_API_URL}/google/gmail/auth?client_id=${user?.id}`;
  };

  const clearChakraJobPoll = () => {
    if (chakraJobPollRef.current !== null) {
      window.clearTimeout(chakraJobPollRef.current);
      chakraJobPollRef.current = null;
    }
  };

  const resetChakraReview = () => {
    clearChakraJobPoll();
    setChakraPreview(null);
    setChakraResult(null);
    setChakraImportJob(null);
  };

  const getChakraAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Missing auth session");
    return token;
  };

  useEffect(() => () => clearChakraJobPoll(), []);

  const refreshChakraImportJob = async (jobId: string) => {
    try {
      const token = await getChakraAuthToken();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/chakra/import/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.detail || "Import status failed");
      }

      setChakraImportJob(payload);

      if (payload.status === "completed") {
        clearChakraJobPoll();
        if (payload.kind === "preview") {
          setChakraPreview(payload.result || null);
          setChakraImportJob(null);
        } else {
          setChakraResult(payload.result || null);
        }
        return;
      }

      if (payload.status === "failed") {
        clearChakraJobPoll();
        setChakraError(payload.error || "Import failed");
        return;
      }

      chakraJobPollRef.current = window.setTimeout(() => {
        void refreshChakraImportJob(jobId);
      }, 3000);
    } catch (err: any) {
      clearChakraJobPoll();
      setChakraError(err?.message || "Import status failed");
    }
  };

  const handleChakraPreview = async () => {
    if (!user) return;

    clearChakraJobPoll();
    setChakraError(null);
    setChakraResult(null);
    setChakraPreview(null);
    setChakraImportJob(null);
    setChakraAction("preview");

    try {
      const token = await getChakraAuthToken();

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/chakra/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: chakraMode,
          phone_number: chakraPhoneNumber || undefined,
          chat_id: chakraChatId || undefined,
          before_date: chakraBeforeDate || undefined,
          dry_run: true,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.detail || "Import failed");
      }

      if (payload?.job_id) {
        setChakraImportJob(payload);
        if (payload.status === "completed") {
          if (payload.kind === "preview") {
            setChakraPreview(payload.result || null);
            setChakraImportJob(null);
          }
        } else if (payload.status === "failed") {
          setChakraError(payload.error || "Import failed");
        } else {
          chakraJobPollRef.current = window.setTimeout(() => {
            void refreshChakraImportJob(payload.job_id);
          }, 3000);
        }
      } else {
        setChakraPreview(payload);
      }
    } catch (err: any) {
      setChakraError(err?.message || "Import failed");
    } finally {
      setChakraAction(null);
    }
  };

  const handleChakraImport = async () => {
    if (!user) return;

    clearChakraJobPoll();
    setChakraError(null);
    setChakraResult(null);
    setChakraImportJob(null);
    setChakraAction("import");

    try {
      const token = await getChakraAuthToken();

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/chakra/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: chakraMode,
          phone_number: chakraPhoneNumber || undefined,
          chat_id: chakraChatId || undefined,
          before_date: chakraBeforeDate || undefined,
          dry_run: false,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.detail || "Import failed");
      }

      setChakraPreview(null);
      if (payload?.job_id) {
        setChakraImportJob(payload);
        if (payload.status === "completed") {
          setChakraResult(payload.result || null);
        } else if (payload.status === "failed") {
          setChakraError(payload.error || "Import failed");
        } else {
          chakraJobPollRef.current = window.setTimeout(() => {
            void refreshChakraImportJob(payload.job_id);
          }, 3000);
        }
      } else {
        setChakraResult(payload);
      }
    } catch (err: any) {
      setChakraError(err?.message || "Import failed");
    } finally {
      setChakraAction(null);
    }
  };

  const handleSignContract = async () => {
    if (!clientData || !user) return;

    try {
      const response = await fetch(clientData.contract_url);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append("pdf", blob, "contract.pdf");
      formData.append("client_name", clientData.client_signatory_name || `${form.prenom} ${form.nom}`);
      formData.append("client_title", clientData.client_signatory_title || "Client");
      formData.append("client_signed_date", new Date().toISOString());
      formData.append("client_id", clientData.id);
      formData.append("contract_hash", clientData.contract_hash);

      const signedResponse = await fetch(`${import.meta.env.VITE_API_URL}/generate/signed-pdf`, {
        method: "POST",
        body: formData,
      });

      const raw = await signedResponse.text();

      let payload: any;
      try {
        payload = JSON.parse(raw);
      } catch (error) {
        console.error("[signed-pdf] JSON parse error:", error);
        throw new Error("Backend returned non-JSON: " + raw);
      }

      if (payload?.error) {
        const message = typeof payload.error === "string" ? payload.error : JSON.stringify(payload.error);
        throw new Error(message);
      }

      const signedPdfUrl = payload?.url;
      if (!signedPdfUrl) {
        throw new Error("No URL in backend response: " + raw);
      }

      const signedDate = new Date().toISOString();
      const { error } = await supabase
        .from("clients")
        .update({
          client_signature_url: "SIGNED",
          client_signed_date: signedDate,
          client_signed: true,
          contract_url: signedPdfUrl,
          contract_hash: clientData.contract_hash,
        })
        .eq("id", user.id);

      if (error) throw error;

      setShowConfirmation(true);
      setClientData((previous: any) => ({
        ...previous,
        client_signature_url: "SIGNED",
        contract_signed_date: signedDate,
        client_signed: true,
        contract_url: signedPdfUrl,
        contract_hash: clientData.contract_hash,
      }));
    } catch (err: any) {
      console.error("[handleSignContract] ERROR:", err);
      alert("An error occurred while signing the contract. Check console for details.");
    }
  };

  const confirmPdpaOptout = async () => {
    if (!user || !clientData) return;

    setPdpaSaving(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update(buildClinicConfirmationPayload({ actorId: user.id, actorName: user.email }))
        .eq("id", clientData.id);
      if (error) throw error;

      const { error: eventError } = await supabase.from("pdpa_optout_events").insert(
        buildPdpaOptoutEvent({
          clientId: clientData.id,
          action: "clinic_confirmed",
          actorId: user.id,
          actorRole: "clinic",
          actorName: user.email,
          attestation: buildAttestationRecord(),
        })
      );
      if (eventError) throw eventError;

      setPdpaAckChecked(false);
      await loadProfile();
    } catch (err: any) {
      console.error("[PDPA] opt-out confirmation failed:", err);
      alert("Could not save the PDPA confirmation: " + (err?.message || err));
    } finally {
      setPdpaSaving(false);
    }
  };

  if (user === undefined || loading) {
    return <div className="p-6 text-gray-500">Chargement du profil…</div>;
  }

  if (user === null) {
    return <div className="p-6 text-gray-500">Non connecté.</div>;
  }

  const appearanceUnavailable = !clientData;
  const labelsUnavailable = !clientData || !statusTagsEnabled;

  return (
    <div className={containerClass}>
      {!isEmbedded && <h1 className="mb-6 text-2xl font-semibold text-primary">👤 My profile</h1>}

      {showBlockedAnswersSection && (
        clientData?.id ? (
          <BlockedAnswers clientId={clientData.id} embedded />
        ) : (
          <p className="text-sm text-slate-500">Loading blocked answers…</p>
        )
      )}

      {showProfileSection && (
        <>
          <div className={cardClass}>
            {(
              [
                { k: "prenom", lbl: "First name" },
                { k: "nom", lbl: "Last name" },
                { k: "telephone", lbl: "Phone" },
                { k: "email", lbl: "Email" },
                { k: "newPassword", lbl: "New password" },
              ] as const
            ).map(({ k, lbl }) => (
              <div className="mb-4" key={k}>
                <label className="mb-1 block text-sm font-medium text-gray-700">{lbl}</label>
                {editing ? (
                  <input
                    type={k.includes("password") ? "password" : "text"}
                    className="w-full rounded border px-3 py-2"
                    value={(form as any)[k] || ""}
                    onChange={(e) => setForm((previous) => ({ ...previous, [k]: e.target.value }))}
                  />
                ) : (
                  <div className="text-sm text-gray-800">
                    {k === "newPassword" ? <em>••••••••</em> : (form as any)[k] || <span className="text-gray-400">Not filled</span>}
                  </div>
                )}
              </div>
            ))}

            {calendarType === "google" && (
              <div className="mt-6 space-y-3">
                <button
                  onClick={handleGoogleConnect}
                  className="w-full rounded bg-red-500 px-4 py-2 text-white shadow hover:bg-red-600"
                >
                  Connect Google Calendar
                </button>

                <button
                  onClick={handleGmailConnect}
                  className="w-full rounded bg-blue-500 px-4 py-2 text-white shadow hover:bg-blue-600"
                >
                  Connect Gmail
                </button>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              {editing ? (
                <>
                  <button
                    onClick={saveProfile}
                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
                  >
                    Save
                  </button>
                  <button onClick={() => setEditing(false)} className="text-gray-600 hover:underline">
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} className="text-blue-600 hover:underline">
                  Modify my profile
                </button>
              )}
            </div>
          </div>

          {clientData && clientData.contract_url && clientData.client_signed !== true && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4">
              <h2 className="mb-2 text-sm font-semibold text-yellow-800">📄 Contract pending signature</h2>
              <div className="flex items-center gap-4">
                <a
                  href={clientData.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm"
                  onClick={() => setHasViewedContract(true)}
                >
                  View contract
                </a>

                <div className="group relative">
                  <button
                    disabled={!hasViewedContract}
                    onClick={handleSignContract}
                    className={`rounded px-4 py-2 text-sm shadow transition ${
                      hasViewedContract
                        ? "cursor-pointer bg-green-600 text-white hover:bg-green-700"
                        : "cursor-not-allowed bg-gray-300 text-gray-500"
                    }`}
                  >
                    Sign contract
                  </button>

                  {!hasViewedContract && (
                    <div className="absolute bottom-full left-1/2 mb-2 w-max -translate-x-1/2 transform rounded bg-gray-800 px-3 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                      Please view your contract first
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {clientData && (
            <div className={cardClass}>
              <h2 className="mb-2 text-lg font-semibold text-primary">🔐 PDPA storage notices</h2>
              {clientData.pdpa_consent_messages_enabled !== false ? (
                <p className="text-sm text-gray-600">
                  Patients automatically receive a one-time PDPA notice informing them that their
                  data is stored securely. If your clinic prefers to inform patients by its own
                  means, contact ConnectLah — you will then be asked to confirm this choice here.
                </p>
              ) : clientData.pdpa_optout_confirmed_at ? (
                <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                  ✅ Automatic PDPA notices are disabled. Confirmed on{" "}
                  {new Date(clientData.pdpa_optout_confirmed_at).toLocaleDateString("en-SG", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}{" "}
                  by {clientData.pdpa_optout_confirmed_by_name || clientData.pdpa_optout_confirmed_by}.
                  Your clinic informs patients of data storage by its own means.
                </div>
              ) : (
                <div>
                  <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Automatic PDPA data-storage notices have been disabled at your clinic&apos;s
                    request. Please confirm the attestation below. Your confirmation is recorded in
                    the compliance register together with your identity and the date.
                  </div>
                  <p className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm italic text-gray-700">
                    {PDPA_OPTOUT_ATTESTATION_TEXT}
                  </p>
                  <label className="mb-3 flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={pdpaAckChecked}
                      onChange={(e) => setPdpaAckChecked(e.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      I am authorized to act on behalf of the clinic and I confirm the attestation
                      above.
                    </span>
                  </label>
                  <button
                    disabled={!pdpaAckChecked || pdpaSaving}
                    onClick={confirmPdpaOptout}
                    className={`rounded px-4 py-2 text-sm shadow transition ${
                      pdpaAckChecked && !pdpaSaving
                        ? "cursor-pointer bg-primary text-white hover:bg-opacity-90"
                        : "cursor-not-allowed bg-gray-300 text-gray-500"
                    }`}
                  >
                    {pdpaSaving ? "Saving…" : "Confirm on behalf of the clinic"}
                  </button>
                </div>
              )}
            </div>
          )}

          {(clientData?.whatsapp_provider || "").toLowerCase() === "chakra" && (
            <div className={cardClass}>
              <h2 className="mb-4 text-lg font-semibold text-primary">Chakra history import</h2>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Mode</label>
                  <select
                    className="w-full rounded border px-3 py-2"
                    value={chakraMode}
                    onChange={(e) => {
                      setChakraMode(e.target.value as "phone" | "chat" | "all");
                      resetChakraReview();
                    }}
                  >
                    <option value="phone">Phone</option>
                    <option value="chat">Chat</option>
                    <option value="all">All chats</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone number</label>
                  <input
                    className="w-full rounded border px-3 py-2"
                    placeholder="+6591234567"
                    value={chakraPhoneNumber}
                    onChange={(e) => {
                      setChakraPhoneNumber(e.target.value);
                      resetChakraReview();
                    }}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Chat ID</label>
                  <input
                    className="w-full rounded border px-3 py-2"
                    placeholder="chat_123"
                    value={chakraChatId}
                    onChange={(e) => {
                      setChakraChatId(e.target.value);
                      resetChakraReview();
                    }}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Import messages before date</label>
                  <input
                    type="date"
                    className="w-full rounded border px-3 py-2"
                    value={chakraBeforeDate}
                    onChange={(e) => {
                      setChakraBeforeDate(e.target.value);
                      resetChakraReview();
                    }}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Messages on or after this date are excluded.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleChakraPreview}
                    disabled={chakraLoading || chakraMissingRequiredInput}
                    className={`rounded px-4 py-2 text-white ${
                      chakraLoading || chakraMissingRequiredInput ? "bg-gray-400" : "bg-primary hover:bg-opacity-90"
                    }`}
                  >
                    {chakraPreviewRunning ? "Preview running..." : chakraAction === "preview" ? "Starting preview..." : "Preview Import"}
                  </button>

                  <button
                    onClick={handleChakraImport}
                    disabled={chakraLoading || !chakraPreview || chakraPreview.importable === 0}
                    className={`rounded px-4 py-2 text-white ${
                      chakraLoading || !chakraPreview || chakraPreview.importable === 0
                        ? "bg-gray-400"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {chakraImportRunning ? "Import running..." : chakraAction === "import" ? "Starting import..." : "Confirm Import"}
                  </button>
                </div>

                {chakraPreview && (
                  <div className="border-t border-slate-200 pt-4">
                    <div className="mb-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-700">
                      <span>Fetched: <strong>{chakraPreview.total_fetched}</strong></span>
                      <span>Ready: <strong>{chakraPreview.importable}</strong></span>
                      <span>Skipped: <strong>{chakraPreview.skipped}</strong></span>
                      <span>Duplicates: <strong>{chakraPreview.duplicates}</strong></span>
                      {typeof chakraPreview.date_filtered === "number" && chakraPreview.before_date && (
                        <span>Date excluded: <strong>{chakraPreview.date_filtered}</strong></span>
                      )}
                      {typeof chakraPreview.total_chats === "number" && (
                        <span>
                          Chats scanned: <strong>{chakraPreview.scanned_chats ?? 0}</strong> /{" "}
                          <strong>{chakraPreview.total_chats}</strong>
                        </span>
                      )}
                    </div>

                    {chakraPreview.scan_complete === false && (
                      <p className="mb-3 text-sm text-amber-700">
                        Preview limited to {chakraPreview.scanned_chats ?? chakraPreview.chat_limit} chat(s).
                        Confirm Import will continue in the background for all chats.
                      </p>
                    )}

                    {chakraPreview.message_limit_reached && (
                      <p className="mb-3 text-sm text-amber-700">
                        Preview reached its message scan limit.
                      </p>
                    )}

                    {chakraPreview.skipped_chats?.length > 0 && (
                      <p className="mb-3 text-sm text-amber-700">
                        {chakraPreview.skipped_chats.length} chat(s) skipped after Chakra returned an error.
                      </p>
                    )}

                    <div className="max-h-72 divide-y divide-slate-200 overflow-y-auto border-y border-slate-200">
                      {chakraPreview.messages.length === 0 ? (
                        <p className="py-3 text-sm text-gray-500">No new messages ready to import.</p>
                      ) : (
                        chakraPreview.messages.map((message) => (
                          <div key={message.chakra_message_id} className="py-3 text-sm">
                            <div className="mb-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                              <span>{message.created_at}</span>
                              <span>{message.role}</span>
                              <span>{message.utilisateur}</span>
                            </div>
                            <p className="text-gray-800">{message.text_preview || "(empty message)"}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {chakraImportJob && chakraJobRunning && (
                  <div className="rounded border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                    <p>
                      {chakraJobStatusLabel}: <strong>{chakraImportJob.status}</strong>
                    </p>
                    <p className="mt-1 break-all text-xs text-blue-700">Job ID: {chakraImportJob.job_id}</p>
                  </div>
                )}

                {chakraResult && (
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>Total imported: <strong>{chakraResult.imported}</strong></p>
                    <p>Total skipped: <strong>{chakraResult.skipped}</strong></p>
                    {typeof chakraResult.date_filtered === "number" && chakraResult.before_date && (
                      <p>Date excluded: <strong>{chakraResult.date_filtered}</strong></p>
                    )}
                    {typeof chakraResult.total_chats === "number" && (
                      <p>
                        Chats scanned: <strong>{chakraResult.scanned_chats ?? 0}</strong> /{" "}
                        <strong>{chakraResult.total_chats}</strong>
                      </p>
                    )}
                    {chakraResult.skipped_chats?.length ? (
                      <p>Chats skipped: <strong>{chakraResult.skipped_chats.length}</strong></p>
                    ) : null}
                  </div>
                )}

                {chakraError && <p className="text-sm text-red-600">{chakraError}</p>}
              </div>
            </div>
          )}
        </>
      )}

      {showHighlightsSection && (
        appearanceUnavailable ? (
          <div className={cardClass}>
            <p className="text-sm text-slate-500">
              Conversation appearance settings are unavailable for this clinic.
            </p>
          </div>
        ) : (
          <div className={cardClass}>
            <h2 className="mb-4 text-lg font-semibold text-primary">Conversation Highlights</h2>
            <p className="mb-4 text-sm text-gray-500">
              Configure the highlight colors for conversations in your list. Tag-based colors
              (booking, emergency, refill) take priority over these settings.
            </p>

            <div className="space-y-4">
              <HighlightColorPicker
                label="Unread conversations"
                value={highlightUnread}
                onChange={setHighlightUnread}
              />
              <HighlightColorPicker
                label="Read conversations"
                value={highlightRead}
                onChange={setHighlightRead}
              />

              <button
                disabled={highlightSaving}
                onClick={async () => {
                  if (!clientData) return;
                  setHighlightSaving(true);
                  const { error } = await supabase
                    .from("clients")
                    .update({
                      highlight_unread: highlightUnread || null,
                      highlight_read: highlightRead || null,
                    })
                    .eq("id", clientData.id);
                  setHighlightSaving(false);
                  if (error) {
                    alert("Error saving highlight settings: " + error.message);
                  }
                }}
                className={`rounded px-4 py-2 text-white ${
                  highlightSaving ? "bg-gray-400" : "bg-primary hover:bg-opacity-90"
                }`}
              >
                {highlightSaving ? "Saving..." : "Save highlights"}
              </button>
            </div>
          </div>
        )
      )}

      {showLabelsSection && (
        labelsUnavailable ? (
          <div className={cardClass}>
            <p className="text-sm text-slate-500">
              Conversation labels are not enabled for this clinic.
            </p>
          </div>
        ) : (
          <div className={cardClass}>
            <h2 className="mb-1 text-lg font-semibold text-primary">Conversation Labels</h2>
            <p className="mb-4 text-sm text-gray-500">
              Define custom labels to tag and filter conversations. Use right-click on desktop, or
              long-press on mobile, to assign a label.
            </p>

            <div className="mb-4 space-y-3">
              {clinicStatuses.map((status, index) => (
                <div key={status.id || index} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={status.label}
                    placeholder="Label name"
                    onChange={(e) => {
                      const next = [...clinicStatuses];
                      next[index] = { ...next[index], label: e.target.value };
                      setClinicStatuses(next);
                    }}
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {HIGHLIGHT_COLORS.filter((color) => color.value !== "").map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        title={color.label}
                        onClick={() => {
                          const next = [...clinicStatuses];
                          next[index] = { ...next[index], color: color.value };
                          setClinicStatuses(next);
                        }}
                        className={`h-5 w-5 rounded-full transition-all ${color.swatch} ${
                          status.color === color.value
                            ? "scale-110 ring-2 ring-primary ring-offset-1"
                            : "hover:scale-110"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setClinicStatuses(clinicStatuses.filter((_, itemIndex) => itemIndex !== index))}
                    className="text-xs font-medium text-red-400 transition hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() =>
                  setClinicStatuses([
                    ...clinicStatuses,
                    { id: `status_${Date.now()}`, label: "", color: "blue" },
                  ])
                }
                className="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-slate-600 transition hover:border-primary hover:text-primary"
              >
                + Add label
              </button>
              <button
                type="button"
                disabled={statusesSaving}
                onClick={async () => {
                  if (!clientData) return;
                  setStatusesSaving(true);
                  const { error } = await supabase
                    .from("clients")
                    .update({ conversation_statuses: clinicStatuses })
                    .eq("id", clientData.id);
                  setStatusesSaving(false);
                  if (error) {
                    alert("Error saving labels: " + error.message);
                  }
                }}
                className={`rounded px-4 py-2 text-sm text-white ${
                  statusesSaving ? "bg-gray-400" : "bg-primary hover:bg-opacity-90"
                }`}
              >
                {statusesSaving ? "Saving..." : "Save labels"}
              </button>
            </div>
          </div>
        )
      )}

      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-sm rounded-xl bg-white p-6 text-center shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-gray-800">✅ Contract signed!</h2>
            <p className="mb-4 text-gray-600">
              Your contract is signed, you'll find it in the billing & admin section.
            </p>
            <button
              onClick={() => setShowConfirmation(false)}
              className="rounded bg-primary px-4 py-2 text-white hover:bg-blue-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
